import React, { useCallback } from "react";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import {
  DEFAULT_COUNTRY,
  normalizePhoneInput,
  repairPhoneValue,
  toE164Value,
} from "../../utils/phoneNumber";

/**
 * Drop-in replacement for react-phone-number-input's `PhoneInput` that accepts
 * a number however it happens to be written.
 *
 * George Bottas, Monday 12974935307 (CRM-2026-00025): the raw input only
 * accepted E.164. Pasting "0412 345 678" on top of the "+61" prefix produced
 * "+610412345678", and pasting a number that already carried its own "+" made
 * the input's parser discard the "+" and everything after it - so agents were
 * retyping numbers by hand.
 *
 * Three things change here:
 *  - paste is intercepted and the whole field is replaced with the normalised
 *    number, so a pasted number never has to be repaired by hand;
 *  - every keystroke runs through `repairPhoneValue`, so typing a local number
 *    straight after the "+61" prefix drops the trunk zero by itself;
 *  - a stored local number is converted for display instead of being rejected.
 *
 * `onChange` still receives an E.164 string (or `undefined`), exactly as before.
 */
const PhoneField = ({
  value,
  onChange,
  defaultCountry = DEFAULT_COUNTRY,
  onPaste,
  onBlur,
  ...rest
}) => {
  const country = defaultCountry || DEFAULT_COUNTRY;

  const handleChange = useCallback(
    (next) => {
      if (onChange) onChange(repairPhoneValue(next, country));
    },
    [onChange, country]
  );

  const handlePaste = useCallback(
    (event) => {
      const clipboard = event.clipboardData || window.clipboardData;
      const text = clipboard ? clipboard.getData("text") : "";
      const normalized = normalizePhoneInput(text, country);
      // Only take over when we recognise a complete number. Anything else
      // (a fragment, an area code) falls through to the default behaviour.
      if (normalized) {
        event.preventDefault();
        if (onChange) onChange(normalized);
      }
      if (onPaste) onPaste(event);
    },
    [onChange, onPaste, country]
  );

  const handleBlur = useCallback(
    (event) => {
      const repaired = repairPhoneValue(value, country);
      if (repaired !== value && onChange) onChange(repaired);
      if (onBlur) onBlur(event);
    },
    [onChange, onBlur, value, country]
  );

  return (
    <PhoneInput
      international
      defaultCountry={country}
      {...rest}
      value={toE164Value(value, country)}
      onChange={handleChange}
      onPaste={handlePaste}
      onBlur={handleBlur}
    />
  );
};

export default PhoneField;
